import { format, isSameDay, parseISO } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

import ActionStatusText from '../../components/ui/ActionStatusText';
import ErrorMessage from '../../components/ui/ErrorMessage';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { svExtended } from '../../config/custom-locales';
import { SUCCESS_TIMEOUT } from '../../config/modal-config';
import { useModalAction, useModalEntryType, useModalNavigation } from '../../context/ModalControllerContext';
import { useModalEntry } from '../../context/ModalEntryContext';
import { buildDatePickerPayload } from '../../helpers';
import useSessionDatesForMonth from '../../hooks/data/exercise-sessions/useSessionDatesForMonth';
import useMealDatesForMonth from '../../hooks/data/meals/useMealDatesForMonth';
import useDatePickerAction from '../../hooks/modal/useDatePickerAction';
import useOptionalHook from '../../hooks/utils/useOptionalHook';
import ModalHeader from '../components/ModalHeader';
import ModalMenu from '../components/ModalMenu';

const ModalDatePicker = ({
    currentDay,
    favoriteId,
    favoriteName,
    onRequestClose,
    entryType: externalEntryType,
    action: externalAction
}) => {
    const modalEntry = useOptionalHook(useModalEntry);
    const modalEntryType = useOptionalHook(useModalEntryType);
    const modalAction = useOptionalHook(useModalAction);
    const navigation = useOptionalHook(useModalNavigation);

    // RESOLVED VALUES
    const entryType = externalEntryType ?? modalEntryType?.entryType;
    const action = externalAction ?? modalAction?.action;
    const selectedMealId = modalEntry?.selectedEntryId;
    const selectedEntryId = favoriteId ?? selectedMealId;

    const handleClose = useCallback(() => {
        onRequestClose?.();
        navigation?.close();
    }, [onRequestClose, navigation]);

    const [selectedDate, setSelectedDate] = useState(() => new Date(currentDay));
    const [localActive, setLocalActive] = useState(() => entryType);
    const [isCompleted, setIsCompleted] = useState(false);

    const closeTimerRef = useRef(null);

    // DERIVED

    const isExercise = localActive === 'exercise';
    const isAdd = action === 'add';
    const isMove = action === 'move';

    const actionVerb = useMemo(() => {
        if (isAdd) return 'Lägg till';
        if (isMove) return 'Flytta';
        return 'Kopiera';
    }, [isAdd, isMove]);

    const initialMealType = useMemo(() => {
        if (typeof entryType === 'number') return entryType;
        return null;
    }, [entryType]);

    const [targetMealType, setTargetMealType] = useState(initialMealType);

    const currentDateObj = useMemo(() => parseISO(currentDay), [currentDay]);
    const targetDate = useMemo(
        () => (selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null),
        [selectedDate]
    );

    const isSameMealType = initialMealType !== null && targetMealType === initialMealType;
    const isSameDate = selectedDate && isSameDay(selectedDate, currentDateObj);

    const noChanges = action !== 'add' && !isExercise && isSameDate && isSameMealType;

    // ACTION

    const {
        triggerAction,
        isProcessing,
        isError: isActionError,
        error: actionError,
        reset
    } = useDatePickerAction({
        action,
        isExercise,
        currentDay,
        selectedEntryId
    });

    // DATE HIGHLIGHTING

    const datesHook = isExercise
        ? useSessionDatesForMonth(currentDay)
        : useMealDatesForMonth(currentDay, targetMealType);

    const { visibleMonth, setVisibleMonth, isLoading, isError: isDatesError, error: datesError } = datesHook;
    const highlightedDates = isExercise ? datesHook.sessionDates : datesHook.mealDates;

    // HANDLERS

    const handleAction = async () => {
        if (!selectedDate || noChanges) return;

        const payload = buildDatePickerPayload({
            isExercise,
            action,
            currentDay,
            targetDate,
            targetMealType,
            selectedEntryId,
            favoriteName
        });

        const ok = await triggerAction(payload);
        if (!ok) return;

        setIsCompleted(true);

        closeTimerRef.current = setTimeout(() => {
            reset();
            handleClose();
        }, SUCCESS_TIMEOUT);
    };

    useEffect(() => () => closeTimerRef.current && clearTimeout(closeTimerRef.current), []);
    useEffect(() => setLocalActive(entryType), [entryType]);

    // UI STATE

    const hasError = isActionError || isDatesError;
    const errorMessage =
        actionError?.message ||
        datesError?.message ||
        'Vi kunde inte ladda kalendern just nu. Försök igen senare.';

    const isLocked = isProcessing || isCompleted;
    const actionBtnDisabled = isLocked || !selectedDate || noChanges;

    if (entryType == null || !action) return null;

    return (
        <>
            <ModalHeader
                title={`${actionVerb} ${isExercise ? 'träning' : 'måltid'}`}
                onClose={handleClose}
                disableButtons={isLocked}
                showBack={false}
            />

            <div className={`modal-body date-picker ${isAdd ? 'add' : ''}`}>
                {!hasError ? (
                    <>
                        <p className="date-picker-text">
                            Välj vilket datum du vill {actionVerb.toLowerCase()} till.
                        </p>

                        <div className="modal-date-picker">
                            {isLoading ? (
                                <LoadingSpinner height={290} />
                            ) : (
                                <DayPicker
                                    mode="single"
                                    selected={selectedDate}
                                    onSelect={(date) => {
                                        if (date) setSelectedDate(date);
                                    }}
                                    locale={svExtended}
                                    month={visibleMonth}
                                    onMonthChange={setVisibleMonth}
                                    modifiers={{
                                        hasDates: highlightedDates?.map(parseISO) ?? []
                                    }}
                                    modifiersClassNames={{
                                        hasDates: 'highlighted-day'
                                    }}
                                />
                            )}
                        </div>
                    </>
                ) : (
                    <ErrorMessage message={errorMessage} />
                )}
            </div>

            <div className="modal-footer date-picker">
                {!isExercise && (
                    <ModalMenu
                        activeMealType={targetMealType}
                        onSelect={(mealType) => {
                            setTargetMealType(mealType);
                            setLocalActive((prev) => (prev === 'exercise' ? prev : `${mealType}-0`));
                        }}
                    />
                )}

                <button
                    className="btn-green"
                    disabled={actionBtnDisabled}
                    onClick={handleAction}
                    aria-busy={isProcessing}
                >
                    <ActionStatusText
                        isProcessing={isProcessing}
                        isCompleted={isCompleted}
                        processingLabel={isAdd ? 'Lägger till' : isMove ? 'Flyttar' : 'Kopierar'}
                        successLabel={
                            isAdd
                                ? 'Måltiden har lagts till!'
                                : `${isExercise ? 'Träningen' : 'Måltiden'} har ${
                                      isMove ? 'flyttats' : 'kopierats'
                                  }!`
                        }
                        defaultLabel={`${actionVerb} hit`}
                    />
                </button>

                <button className="border-btn-green" onClick={handleClose} disabled={isLocked}>
                    Avbryt
                </button>
            </div>
        </>
    );
};

export default ModalDatePicker;
